import { IconPhotoFilled } from "@tabler/icons-react";
import { motion } from "framer-motion";
import { Fragment, useRef, useState } from "react";
import CarouselIndicatorGroup from "src/components/UI/CarouselIndicators/CarouselIndicatorGroup";
import PaddedView from "src/components/UI/PaddedView/PaddedView";
import { useDeviceContext } from "src/context/device-context";
import classes from "./SaleBanners.module.css";
import { useImageIntersectionObserver } from "src/hooks/useImageIntersectionObserver";

function SaleBannerItem({ item, ...props }) {
  return item.image ? (
    <motion.a
      {...props}
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      key={item.id}
      href={item.link}>
      <img alt="sale-banner" data-src={item.image} />
    </motion.a>
  ) : (
    <div {...props} key={item.id} className={classes.imgError}>
      <div className={classes.frame}>
        <IconPhotoFilled size="100%" color="var(--ui-4)" />
      </div>
    </div>
  );
}

const MAX_BANNERS = 3;

function MobileSaleBanners({ items }) {
  const [currentItem, setCurrentItem] = useState(0);

  const [touchStartX, setTouchStartX] = useState(null);
  const [touchEndX, setTouchEndX] = useState(null);

  const minSwipeLength = 25;

  const onTouchStartHandler = (e) => {
    setTouchEndX(null);
    setTouchStartX(e.targetTouches[0].clientX);
  };

  const onTouchMoveHandler = (e) => setTouchEndX(e.targetTouches[0].clientX);

  const onSwipeHandler = () => {
    const distance = touchStartX - touchEndX;
    const isLeftSwipe = distance > minSwipeLength;
    const isRightSwipe = distance < -minSwipeLength;
    if (isLeftSwipe || isRightSwipe) {
      const indexChange = isLeftSwipe ? 1 : -1;
      let nextBanner = currentItem + indexChange;
      if (nextBanner > MAX_BANNERS - 1) {
        nextBanner = 0;
      } else if (nextBanner < 0) {
        nextBanner = MAX_BANNERS - 1;
      }
      setCurrentItem(nextBanner);
    }
  };

  return (
    <Fragment>
      <ul
        className={`${classes.saleImage} ${classes.container}`}
        onTouchStart={onTouchStartHandler}
        onTouchMove={onTouchMoveHandler}
        onTouchEnd={onSwipeHandler}>
        {items.map((item, i) => (
          <li key={item.id}>
            <SaleBannerItem
              item={item}
              style={{
                pointerEvents: "none",
                ...(i === currentItem ? {} : { display: "none" }),
              }}
            />
          </li>
        ))}
      </ul>
      <CarouselIndicatorGroup count={items.length} current={currentItem} />
    </Fragment>
  );
}

function DesktopSaleBanners({ items }) {
  return (
    <ul
      style={{ pointerEvents: "none" }}
      className={`${classes.saleImageGroup} ${classes.container}`}>
      {items.map((item) => (
        <li key={item.id}>
          <SaleBannerItem item={item} />
        </li>
      ))}
    </ul>
  );
}

function SaleBanners({ saleData }) {
  const { isMobile } = useDeviceContext();
  const containerRef = useRef(null);
  useImageIntersectionObserver(containerRef);

  return (
    <section
      ref={containerRef}
      style={{ marginBottom: "2rem" }}
      className={`${!isMobile ? classes.desktop : classes.mobile}`}>
      {isMobile ? (
        <MobileSaleBanners items={saleData} />
      ) : (
        <PaddedView>
          <DesktopSaleBanners items={saleData} />
        </PaddedView>
      )}
    </section>
  );
}

export default SaleBanners;
